import { api } from '@packages/backend/convex/_generated/api';
import { CONVERSATION_KINDS, zConversationKind } from '@packages/shared';
import { createFileRoute, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { ConversationListCard } from './components/ConversationListCard';
import { SimulateNewConversationDialog } from './components/SimulateNewConversationDialog';
import { formatLabel } from './utils';

const SHOW_SIMULATE = import.meta.env.VITE_STAGE !== 'production';

export const Route = createFileRoute('/a/conversations')({
  staticData: { noPagePadding: true },
  validateSearch: (search) => ({
    kind: zConversationKind.default('sales').parse(search.kind),
  }),
  component: Component,
});

function Component() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const params = useParams({ strict: false });
  const selectedConversationId = params.conversationId ?? null;
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const { data, error, isError, isPending } = useQuery(api.conversations_admin.listConversations, {
    kind: search.kind,
  });

  return (
    <div className="grid h-[calc(100svh-3.5rem)] min-h-0 max-w-full overflow-hidden lg:h-[calc(100svh-60px)] xl:grid-cols-[320px_minmax(0,1fr)]">
      <aside
        className={`flex min-h-0 flex-col gap-3 xl:border-r ${selectedConversationId ? 'hidden xl:flex' : 'flex'}`}
      >
        <div className="flex items-center justify-between gap-2 px-3 pt-3">
          <Tabs
            value={search.kind}
            onValueChange={(value) =>
              void navigate({
                search: { kind: zConversationKind.parse(value) },
                replace: true,
              })
            }
          >
            <TabsList>
              {CONVERSATION_KINDS.map((kind) => (
                <TabsTrigger key={kind} value={kind}>
                  {formatLabel(kind)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {SHOW_SIMULATE && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton aria-label="More actions" size="icon" variant="ghost">
                  <MoreHorizontal className="h-4 w-4" />
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setSimulateDialogOpen(true)}>
                  Simulate new conversation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {simulateDialogOpen && (
          <SimulateNewConversationDialog onClose={() => setSimulateDialogOpen(false)} />
        )}

        {isError && (
          <div className="rounded-md border border-destructive/50 p-3 text-sm text-destructive">
            Error loading conversations. {error.message}
          </div>
        )}

        {isPending ? (
          <div className="flex min-h-0 flex-1 items-center justify-center border-y">
            <LoadingIcon />
          </div>
        ) : (
          <div className="flex flex-col overflow-y-auto border-y [&>*:not(:last-child)]:border-b">
            {data?.map((conversationWrap) => (
              <ConversationListCard
                key={conversationWrap.conversation._id}
                conversationWrap={conversationWrap}
                isSelected={conversationWrap.conversation._id === selectedConversationId}
                kind={search.kind}
              />
            ))}
            {data?.length === 0 && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                No conversations found
              </div>
            )}
          </div>
        )}
      </aside>

      <section
        className={`min-w-0 min-h-0 flex-col overflow-hidden p-4 lg:p-6 ${selectedConversationId ? 'flex' : 'hidden xl:flex'}`}
      >
        <Outlet />
      </section>
    </div>
  );
}
